import {EventListeners, MinzeElement, Reactive} from "minze";
import {FacetFiltersSubscriber} from "../../modules/search/FacetFiltersSubscriber";
import {IFacetFilters} from "../../modules/search/IFacetFilters";
import {IFacet} from "../../modules/search";
import {Locale} from "../../modules/locale";
import { SquidexTextLabels } from "../../modules/squidex/labels";

export interface TjSearchFacets {
  filters: IFacetFilters;
  facets: IFacet[];
  collapsedFacetGroups: {[key: string]: boolean}
}

Locale.getInstance().registerKeySet("fr", "searchFilters", {
  "heading": "Filtres",
  "byTypeGroupTitle": "Par types",
  "byThemeGroupTitle": "Par thème",
});

Locale.getInstance().registerKeySet("en", "searchFilters", {
  "heading": "Filters",
  "byTypeGroupTitle": "By Type",
  "byThemeGroupTitle": "By Theme",
});

export class TjSearchFacets extends MinzeElement {
  attrs = ["data-group-id", "width"];
  reactive: Reactive = [
    ["filters", {}],
    ["facets", []],
    ["collapsedFacetGroups", {}]
  ];
  lang = Locale.language;
  groupTitlePairs: {[key: string]: string} = {
    "category": SquidexTextLabels.getKey("searchFilters.byThemeGroupTitle"),
    "type": SquidexTextLabels.getKey("searchFilters.byTypeGroupTitle")
  }

  facetFiltersSubscriber: FacetFiltersSubscriber | null = null;

  expandFacetsGroup = (event: Event) => {
    const target = (event.target as HTMLElement)?.closest('.facets-list-label') as Element | null;
    const group = target?.getAttribute("data-group-id");
    if (!group) return;
    this.collapsedFacetGroups[group] = !this.collapsedFacetGroups[group];
  }

  renderFacetFilters = (filters: IFacetFilters) => {
    this.filters = filters;
    this.rerender();
  }

  updateFacetFilters = (facets: IFacet[]) => {
    this.facets = facets;
    this.rerender();
  }

  onReady(): void | Promise<void> {
    this.facetFiltersSubscriber = new FacetFiltersSubscriber(
      this.dataGroupId as string || import.meta.env.VITE_ALGOLIA_INDEX_NAME,
      this.renderFacetFilters.bind(this),
      this.updateFacetFilters.bind(this)
    );
    const mainModule = window.TJ_MAIN_MODULE;
    mainModule.getSearchManager().subscribe(this.facetFiltersSubscriber);
    this.facets = this.facetFiltersSubscriber.getFacets();
  }

  onFilterClicked(event: Event, facetFiltersSubscriber: FacetFiltersSubscriber) {
    const dateCard = (event.target as Element)?.closest('.filter') as Element | null;
    facetFiltersSubscriber?.updateFacetFilter(
      dateCard?.getAttribute("data-filter-value") as string,
      dateCard?.getAttribute("data-is-active") as string === "true",
      dateCard?.getAttribute("data-filter-group") as string
    )
  }

  html = () => /* html */ `
    <h3>${SquidexTextLabels.getKey("searchFilters.heading")}</h3>
    <div class="facets">
      ${
        Object.entries(this.filters).map(filterGroupEntry => {
          const [filterGroupKey, filterGroup] = filterGroupEntry;
          const groupLang = filterGroupKey.split('.')?.[filterGroupKey.split('.').length - 1];
          const groupKey = filterGroupKey.split('.')?.[0];
          const groupTitle = this.groupTitlePairs[groupKey];
          if (groupLang !== this.lang) return ``
          return `
            <div class="facets-group ${this.collapsedFacetGroups[filterGroupKey] ? "collapsed" : "expanded"}">
              <div class="facets-list-label" data-group-id="${filterGroupKey}">
                ${groupTitle}
                <tj-label-chevron-icon></tj-label-chevron-icon>
              </div>
              <div class="facets-list-label-border">
                <svg width="100%" height="2" viewBox="0 0 100% 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line y1="1.00024" x2="100%" y2="1.00024" stroke="#133762" stroke-width="2" stroke-dasharray="8 8"/>
                </svg>
              </div>
              
              <div class="facets-list">
                ${filterGroup.map(filter => {
                  const isActive: boolean = !!(
                    this.facets
                      .find(facet => facet.name === filterGroupKey)?.options
                      .find(option => option.value === filter && option.selected)
                  );
                  return `
                    <button
                      class="filter"
                      data-filter-value="${filter}"
                      data-filter-group="${filterGroupKey}"
                      data-is-active="${isActive}"
                    >
                      ${filter} ${isActive ? `<tj-selected-facet-icon></tj-selected-facet-icon>` : ""}
                    </button>
                  `
                }).join("")}
              </div>
            </div>
          `
        }).join("")
      }
    </div>
  `;

  css = () => `
    :host {
      width: ${this.width || "200px"};
    }
    h3 {
      font-weight: 400;
      font-size: 25px;
      line-height: 150%;
      color: #133762;
      margin-top: 0;
      margin-bottom: 24px;
    }
    .facets-list-label {
      font-weight: 500;
      font-size: 16px;
      line-height: 150%;
      color: #133762;
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;
    }
    .facets-list-label-border {
      display: flex;
    }
    tj-label-chevron-icon {
      width: 16px;
      height: 16px;
    }
    .facets-list {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      margin-top: 8px;
      margin-bottom: 40px;
    }
    .filter {
      cursor: pointer;
      font-weight: 500;
      font-size: 14px;
      font-family: inherit;
      line-height: 150%;
      color: #068FC3;
      background: none;
      border: none;
      padding: 0;
      text-align: left;
      display: flex;
      justify-content: space-between;
      width: 100%;
      align-items: center;
      padding: 8px;
      border-radius: 4px;
    }
    .filter:hover {
      background: rgba(6, 143, 195, 0.20);
    }
    tj-selected-facet-icon {
      width: 16px;
      height: 16px;
    }
    .facets-group.collapsed .facets-list {
      display: none;
    }
    .facets-group.collapsed tj-label-chevron-icon {
      transform: rotate(180deg);
    }
  `;

  eventListeners: EventListeners = [
    [".filter", "click", (event) => {
      this.facetFiltersSubscriber && this.onFilterClicked(event, this.facetFiltersSubscriber)
    }],
    [".facets-list-label", "click", this.expandFacetsGroup.bind(this)],
  ];
}
